import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Edit2, Save, Mail } from "lucide-react";

export default function AdminEmailTemplates() {
  const { t } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editLang, setEditLang] = useState("nl");

  useEffect(() => {
    api.get("/admin/email-templates").then((r) => setTemplates(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      await api.put(`/admin/email-templates/${editing.id}`, { subject: editing.subject, body: editing.body });
      toast.success("Template bijgewerkt");
      setEditing(null);
      api.get("/admin/email-templates").then((r) => setTemplates(r.data));
    } catch { toast.error("Fout bij opslaan"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-email-templates-page">
      <h1 className="text-2xl font-bold">{t("admin.email_templates")}</h1>

      <div className="grid gap-4">
        {templates.map((tmpl) => (
          <Card key={tmpl.id} className={`bg-card border-border ${tmpl.active === false ? "opacity-50" : ""}`} data-testid={`template-${tmpl.slug}`}>
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium capitalize">{tmpl.slug?.replace(/_/g, " ")}</p>
                  <p className="text-sm text-muted-foreground">{tmpl.subject?.nl || tmpl.subject}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline">NL/EN/FR</Badge>
                <Switch checked={tmpl.active !== false} onCheckedChange={async (v) => {
                  await api.put(`/admin/email-templates/${tmpl.id}`, { active: v });
                  api.get("/admin/email-templates").then((r) => setTemplates(r.data));
                  toast.success(v ? "Template geactiveerd" : "Template gedeactiveerd");
                }} data-testid={`toggle-template-${tmpl.slug}`} />
                <Button variant="ghost" size="icon" onClick={() => { setEditing({ ...tmpl }); setEditLang("nl"); }} data-testid={`edit-template-${tmpl.slug}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Template: {editing.slug?.replace(/_/g, " ")}</DialogTitle></DialogHeader>
            <Tabs value={editLang} onValueChange={setEditLang}>
              <TabsList><TabsTrigger value="nl">NL</TabsTrigger><TabsTrigger value="en">EN</TabsTrigger><TabsTrigger value="fr">FR</TabsTrigger></TabsList>
              <TabsContent value={editLang} className="space-y-4 pt-2">
                <div>
                  <Label>Onderwerp ({editLang.toUpperCase()})</Label>
                  <Input
                    value={editing.subject?.[editLang] || ""}
                    onChange={(e) => setEditing({...editing, subject: {...editing.subject, [editLang]: e.target.value}})}
                    className="mt-1"
                    data-testid={`template-subject-${editLang}`}
                  />
                </div>
                <div>
                  <Label>Inhoud ({editLang.toUpperCase()})</Label>
                  <Textarea
                    rows={8}
                    value={editing.body?.[editLang] || ""}
                    onChange={(e) => setEditing({...editing, body: {...editing.body, [editLang]: e.target.value}})}
                    className="mt-1 font-mono-data text-sm"
                    data-testid={`template-body-${editLang}`}
                  />
                </div>
              </TabsContent>
            </Tabs>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button>
              <Button onClick={handleSave} className="bg-blue-600" data-testid="save-template-btn"><Save className="w-4 h-4 mr-2" />Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
